import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Send, ShieldCheck, Lock } from "lucide-react";
import { api, fmtDate } from "@/lib/loop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function timeOf(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

const PrivacyNote = () => (
  <div className="flex items-center gap-2 text-[11px] text-slate-500 bg-[#070C1E] border border-white/10 rounded-lg px-3 py-2">
    <Lock className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
    Chat privately on LOOP. Phone numbers and emails are never shared.
  </div>
);

export function ChatThreadView({ thread, me, heightClass = "h-[420px]" }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => {
    api.get(`/chat/threads/${thread.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [thread.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const t = text;
    setText("");
    try {
      await api.post(`/chat/threads/${thread.id}/messages`, { text: t });
      load();
    } catch (e) {
      setText(t);
      toast.error("Message not sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const other = me?.id === thread.buyer_id ? thread.owner_name : thread.buyer_name;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between pb-3 border-b border-white/10">
        <div>
          <div className="text-sm font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#C5A059]" /> {other}
          </div>
          <div className="text-[11px] text-slate-500 font-mono-loop">{thread.property_loop_id} · {thread.property_title}</div>
        </div>
      </div>

      <div className={`${heightClass} overflow-y-auto py-4 space-y-3 pr-1`} data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="text-center text-sm text-slate-500 mt-10">No messages yet. Say hello 👋</div>
        )}
        {msgs.map((m) => {
          const mine = m.sender_id === me?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2 ${mine
                ? "bg-[#C5A059] text-[#070C1E] rounded-br-md"
                : "bg-[#131B35] text-slate-100 border border-white/10 rounded-bl-md"}`}>
                <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-[#070C1E]/60" : "text-slate-500"}`}>{timeOf(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="pt-3 border-t border-white/10 space-y-2">
        <div className="flex gap-2">
          <Input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder="Type a message…"
            className="bg-[#131B35] border-white/10 text-white"
          />
          <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()}
            className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] shrink-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <PrivacyNote />
      </div>
    </div>
  );
}

export function ChatDialog({ property, user, nav, mobile }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);

  // Owners cannot chat with themselves on their own listing.
  if (user && property.owner_id === user.id) return null;

  const openChat = async () => {
    if (!user) return nav("/auth");
    try {
      const { data } = await api.post("/chat/start", { property_id: property.id });
      setThread(data);
      setOpen(true);
    } catch {
      nav("/auth");
    }
  };

  return (
    <>
      <Button data-testid={mobile ? "chat-owner-btn-mobile" : "chat-owner-btn"} onClick={openChat}
        className={`${mobile ? "flex-1" : ""} bg-transparent border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#070C1E]`}>
        <MessageCircle className="w-4 h-4 mr-2" /> {mobile ? "Chat" : "Chat with Owner"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B132B] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>Message the owner</DialogTitle></DialogHeader>
          {thread && <ChatThreadView thread={thread} me={user} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ChatInbox({ me }) {
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/chat/threads").then((r) => setThreads(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  if (threads.length === 0) {
    return (
      <div className="text-center py-16">
        <MessageCircle className="w-10 h-10 text-slate-600 mx-auto" />
        <p className="mt-3 text-slate-500">No conversations yet.</p>
        <Button data-testid="chat-browse-btn" onClick={() => nav("/search")} className="mt-4 bg-[#C5A059] text-[#070C1E] font-semibold">Browse properties</Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 space-y-2 max-h-[520px] overflow-y-auto">
        {threads.map((t) => {
          const other = me?.id === t.buyer_id ? t.owner_name : t.buyer_name;
          const isActive = active?.id === t.id;
          return (
            <button key={t.id} data-testid={`chat-thread-${t.property_loop_id}`} onClick={() => setActive(t)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${isActive ? "bg-[#131B35] border-[#C5A059]/40" : "bg-[#0B132B] border-white/10 hover:border-white/20"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white truncate">{other}</span>
                {t.unread > 0 && <span className="text-[10px] bg-[#C5A059] text-[#070C1E] font-bold rounded-full px-2 py-0.5">{t.unread}</span>}
              </div>
              <div className="text-[11px] text-slate-500 font-mono-loop truncate">{t.property_loop_id}</div>
              <div className="text-xs text-slate-400 truncate mt-1">{t.last_message || "No messages yet"}</div>
            </button>
          );
        })}
      </div>
      <div className="lg:col-span-2 bg-[#0B132B] border border-white/10 rounded-2xl p-5">
        {active ? <ChatThreadView thread={active} me={me} heightClass="h-[440px]" />
          : <div className="h-[440px] flex items-center justify-center text-slate-500 text-sm">Select a conversation to start chatting</div>}
      </div>
    </div>
  );
}
